/**
 * FirebaseFileUploader for React
 */

import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { v4 as generateRandomID } from "uuid";
import firebase from "firebase/app";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import {
  Modal,
  ModalContent,
  ModalOverlay,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import { Button } from "../components/Button";

const generateRandomFilename = (): string => generateRandomID();

function extractExtension(filename: string): string {
  let ext = /(?:\.([^.]+))?$/.exec(filename);
  if (ext != null && ext[0] != null) {
    return ext[0];
  } else {
    return "";
  }
}

function urlToFile(url: string, filename: string, mimeType: string) {
  return fetch(url)
    .then(function (res) {
      return res.arrayBuffer();
    })
    .then(function (buf) {
      return new File([buf], filename, { type: mimeType });
    });
}

type FirebaseFileUploaderProps = {
  storageRef: firebase.storage.Reference;
  onUploadStart?: (file: File, task: firebase.storage.UploadTask) => void;
  onProgress?: (progress: number, task: firebase.storage.UploadTask) => void;
  onUploadSuccess?: (
    filename: string,
    task: firebase.storage.UploadTask
  ) => void;
  onUploadError?: (
    error: firebase.storage.FirebaseStorageError,
    task: firebase.storage.UploadTask
  ) => void;
  filename?: string | ((file: File) => string);
  metadata?: firebase.storage.UploadMetadata;
  randomizeFilename?: boolean;
  style?: React.CSSProperties;
  hidden?: boolean;
  // TODO: Add all default input props
  id?: string;
  accept?: string;
};

export const FirebaseFileUpload: FC<FirebaseFileUploaderProps> = (props) => {
  const { hidden, accept, onUploadStart } = props;
  const uploadTasks = useRef<firebase.storage.UploadTask[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [file, setFile] = useState<File>();

  const cancelRunningUploads = useCallback(() => {
    uploadTasks.current.forEach((task) => {
      if (task.snapshot.state === "running") {
        task.cancel();
      }
    });
  }, []);

  useEffect(() => cancelRunningUploads, [cancelRunningUploads]);

  const getFileName = (file: File) => {
    const { filename, randomizeFilename } = props;

    if (typeof filename === "string") {
      return filename;
    }

    if (typeof filename === "function") {
      return filename(file);
    }

    if (randomizeFilename) {
      return generateRandomFilename();
    }

    return file.name;
  };

  const removeTask = (task: firebase.storage.UploadTask) => {
    const index = uploadTasks.current.findIndex(
      (uploadTask) => uploadTask === task
    );
    uploadTasks.current.splice(index, 1);
  };

  const startUpload = (file: File) => {
    const { storageRef, metadata } = props;
    const filename = getFileName(file) + extractExtension(file.name);

    const task = storageRef.child(filename).put(file, metadata);

    if (onUploadStart) {
      onUploadStart(file, task);
    }

    task.on(
      "state_changed",
      (snapshot) =>
        props.onProgress &&
        props.onProgress(
          Math.round((100 * snapshot.bytesTransferred) / snapshot.totalBytes),
          task
        ),
      (error) => props.onUploadError && props.onUploadError(error, task),
      () => {
        removeTask(task);
        return (
          props.onUploadSuccess &&
          props.onUploadSuccess(task.snapshot.ref.name, task)
        );
      }
    );
    uploadTasks.current.push(task);
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    // User cancelled the file selection popup
    if (!files) {
      return null;
    }

    setFile(files[0]);
    onOpen();
  };

  const inputStyle = !!hidden
    ? Object.assign({}, props.style, {
        width: "0.1px",
        height: "0.1px",
        opacity: 0,
        overflow: "hidden",
        position: "absolute",
        zIndex: -1,
      })
    : props.style;

  const [cropper, setCropper] = useState<any>();
  const handleCropAndUpload = async () => {
    if (typeof cropper !== "undefined" && file) {
      const dataURL: string = cropper.getCroppedCanvas().toDataURL();
      const croppedFile = await urlToFile(dataURL, file.name, file.type);

      // Only supporting single file upload for now
      startUpload(croppedFile);
      onClose();
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent width="100%" padding="12">
          <Stack>
            {file && (
              <Cropper
                src={URL.createObjectURL(file)}
                // Cropper.js options
                initialAspectRatio={1}
                guides={true}
                onInitialized={(instance) => {
                  setCropper(instance);
                }}
              />
            )}
            <Button onClick={handleCropAndUpload} variant="pulpo">
              Upload
            </Button>
          </Stack>
        </ModalContent>
      </Modal>
      <input
        type="file"
        onChange={handleFileSelection}
        accept={accept}
        style={inputStyle}
      />
    </>
  );
};

type CustomFirebaseUploaderProps = FirebaseFileUploaderProps & {
  className?: string;
};
export const CustomFirebaseUploader: FC<CustomFirebaseUploaderProps> = (
  props
) => {
  const { className = "", children, ...restProps } = props;
  return (
    <label className={className}>
      {children}
      <FirebaseFileUpload hidden {...restProps} />
    </label>
  );
};
